/**
 * Shared core for the live-chart-bucket realtime helpers.
 *
 * `useGetMarketChart` caches an ordered array of OHLCV/line time buckets.
 * Both the bonding-curve helper (`updateLiveBucket`) and the AMM helper
 * (`updateLiveBucketFromAmm`) need the SAME bucket-patching logic — resolve
 * the interval window, locate the event-time bucket, then patch it
 * in place or insert a fresh sparse bucket. Only the venue-specific value
 * extraction differs (post-trade spot or reserves and their derived cap),
 * so that part stays in each wrapper and the rest lives here.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

import type { PagedData } from '../types/PagedData.js';

interface ChartRowBase {
  readonly bucket: string;
}

export interface LineChartRow extends ChartRowBase {
  readonly value: string;
}

export interface OhlcvChartRow extends ChartRowBase {
  readonly open: string;
  readonly high: string;
  readonly low: string;
  readonly close: string;
}

export type ChartRow = LineChartRow | OhlcvChartRow;

export interface BucketPatchArgs {
  readonly liveWindowSeconds?: number;
  readonly interval:
    | '1s'
    | '5s'
    | '15s'
    | '30s'
    | '1m'
    | '5m'
    | '15m'
    | '30m'
    | '1h'
    | '2h'
    | '4h'
    | '1d'
    | '1w'
    | '1mo'
    | 'all';
  readonly chartType: 'ohlcv' | 'line';
  readonly from?: string;
}

export interface ChartQueryArgs extends BucketPatchArgs {
  readonly market?: string;
  readonly virtualQuoteD6?: string;
  readonly outcomeIndex: number | string;
  readonly property: 'price' | 'market_cap' | 'reserves';
  readonly to?: string;
}

export function throwChartRealtimeValueUnavailable(reason: string): never {
  throw new Error(`chart realtime value unavailable: ${reason}`);
}

export interface OptionalChartQueryArgs {
  readonly liveWindowSeconds?: string;
  readonly virtualQuoteD6?: string;
  readonly pubkey?: string;
  readonly outcomeIndex?: number | string;
  readonly interval?: string;
  readonly property?: string;
  readonly chartType?: string;
  readonly from?: string;
  readonly to?: string;
}

export function normalizeChartQueryArgs(args: OptionalChartQueryArgs): ChartQueryArgs {
  const property = normalizeChartProperty(args.property);
  const liveWindowSeconds =
    args.liveWindowSeconds === undefined ? undefined : Number(args.liveWindowSeconds);
  if (
    liveWindowSeconds !== undefined &&
    (!Number.isSafeInteger(liveWindowSeconds) || liveWindowSeconds <= 0)
  ) {
    throw new Error('chart live window must be a positive integer');
  }
  return {
    ...(args.pubkey !== undefined ? { market: args.pubkey } : {}),
    ...(args.virtualQuoteD6 === undefined ? {} : { virtualQuoteD6: args.virtualQuoteD6 }),
    outcomeIndex: args.outcomeIndex ?? 0,
    ...(liveWindowSeconds === undefined ? {} : { liveWindowSeconds }),
    interval: normalizeChartInterval(args.interval),
    property,
    chartType: normalizeChartType(args.chartType, property),
    ...(args.from !== undefined ? { from: args.from } : {}),
    ...(args.to !== undefined ? { to: args.to } : {}),
  };
}

function normalizeChartInterval(value: string | undefined): ChartQueryArgs['interval'] {
  switch (value) {
    case '1s':
    case '5s':
    case '15s':
    case '30s':
    case '1m':
    case '5m':
    case '15m':
    case '30m':
    case '1h':
    case '2h':
    case '4h':
    case '1d':
    case '1w':
    case '1mo':
    case 'all':
      return value;
    default:
      return '1h';
  }
}

function normalizeChartProperty(value: string | undefined): ChartQueryArgs['property'] {
  switch (value) {
    case 'price':
    case 'market_cap':
    case 'reserves':
      return value;
    default:
      return 'price';
  }
}

function normalizeChartType(
  value: string | undefined,
  property: ChartQueryArgs['property'],
): ChartQueryArgs['chartType'] {
  if (value === 'ohlcv' || value === 'line') return value;
  return property === 'price' ? 'ohlcv' : 'line';
}

export interface ChartClock {
  nowSeconds(): number;
}

export const SYSTEM_CHART_CLOCK: ChartClock = {
  nowSeconds: () => Math.floor(Date.now() / 1000),
};

export const INTERVAL_SECONDS = {
  '1s': 1,
  '5s': 5,
  '15s': 15,
  '30s': 30,
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1h': 3600,
  '2h': 7200,
  '4h': 14400,
  '1d': 86400,
  '1w': 604800,
  '1mo': 2592000,
} as const;

// Unix epoch starts on Thursday; Timescale's default weekly origin is Monday UTC.
const TIMESCALE_WEEK_ORIGIN_SECONDS = 4 * INTERVAL_SECONDS['1d'];

export function resolveChartIntervalSeconds(
  args: Pick<BucketPatchArgs, 'interval'>,
  backendBucketSeconds?: number,
): number | null {
  if (
    backendBucketSeconds !== undefined &&
    Number.isInteger(backendBucketSeconds) &&
    backendBucketSeconds > 0
  ) {
    return backendBucketSeconds;
  }
  // Adaptive `all` windows must use the exact CAGG width selected by the
  // backend. Re-deriving its thresholds here split the REST and realtime plans.
  if (args.interval === 'all') return null;
  if (!(args.interval in INTERVAL_SECONDS)) return null;
  return INTERVAL_SECONDS[args.interval as keyof typeof INTERVAL_SECONDS];
}

/**
 * Read a field from a decoded realtime event — handles both the flat
 * protobuf-decoded shape (`raw[key]`, the live wire) and the
 * `.payload`-nested shape (`raw.payload[key]`, JSON test fixtures),
 * payload-nested first — mirroring codegen's `compilePayloadPath`.
 */
export function readEventField(raw: Record<string, unknown>, key: string): unknown {
  const nested = raw.payload;
  if (nested !== null && typeof nested === 'object' && key in (nested as object)) {
    return (nested as Record<string, unknown>)[key];
  }
  return raw[key];
}

export function readIntegerString(raw: Record<string, unknown>, key: string): string | null {
  return integerString(readEventField(raw, key));
}

export function readIntegerStringAt(
  raw: Record<string, unknown>,
  key: string,
  index: number,
): string | null {
  const value = readEventField(raw, key);
  if (!Array.isArray(value)) return integerString(value);
  return integerString(value[index]);
}

function integerString(v: unknown): string | null {
  if (v === undefined || v === null) return null;
  if (typeof v === 'bigint') return v.toString();
  if (typeof v === 'number' && Number.isFinite(v)) return String(Math.trunc(v));
  if (typeof v === 'string' && /^-?\d+$/.test(v.trim())) return v.trim();
  return null;
}

export function readEventTsSeconds(raw: Record<string, unknown>): number | null {
  const v = readEventField(raw, 'ts');
  if (typeof v === 'number') return Number.isSafeInteger(v) && v > 0 ? v : null;
  if (typeof v === 'bigint') {
    return v > 0n && v <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(v) : null;
  }
  if (typeof v !== 'string' || !/^\d+$/.test(v)) return null;
  const parsed = BigInt(v);
  return parsed > 0n && parsed <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(parsed) : null;
}

export function readOutcomeIndexArg(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v);
  if (typeof v === 'string') {
    const n = Number(v);
    if (Number.isFinite(n)) return Math.trunc(n);
  }
  return null;
}

export function readEventOutcomeIdx(raw: Record<string, unknown>): number | null {
  const v = readEventField(raw, 'outcomeIdx');
  if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v);
  if (typeof v === 'string') {
    const n = Number(v);
    if (Number.isFinite(n)) return Math.trunc(n);
  }
  return null;
}

function parseBucketSeconds(bucket: string): number | null {
  const ms = Date.parse(bucket);
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000);
}

function bucketStartSeconds(timestamp: number, windowSecs: number): number {
  const origin = windowSecs === INTERVAL_SECONDS['1w'] ? TIMESCALE_WEEK_ORIGIN_SECONDS : 0;
  return Math.floor((timestamp - origin) / windowSecs) * windowSecs + origin;
}

function eventIsAtOrAfterQueryStart(args: Pick<BucketPatchArgs, 'from'>, eventTs: number): boolean {
  const eventMs = eventTs * 1000;
  if (args.from !== undefined) {
    const fromMs = Date.parse(args.from);
    if (!Number.isFinite(fromMs) || eventMs < fromMs) return false;
  }
  return true;
}

/**
 * Patch the cached chart envelope's event-time bucket from a single resolved
 * trade value. `valueRaw` is the RAW integer-string value in the SAME scale
 * the REST/historical buckets use — D9 for `price`, D6 for `reserves` or
 * effective D6 for `market_cap` — stored VERBATIM so
 * realtime-patched buckets match the server rows. (The frontend applies the
 * per-property scale converter uniformly across every row; storing a
 * pre-divided decimal here makes the converter divide a second time,
 * collapsing the live bucket to ~0 and dragging the y-axis range down to
 * zero.) `eventTs` is epoch seconds.
 *
 * Patches an occupied bucket or inserts a sparse bucket in time order, even
 * when NATS delivers it after the bucket closed. Bails when the interval is
 * unknown or nothing changes — keeping
 * the structural-sharing short-circuit intact.
 */
export function applyBucketPatch<Row extends ChartRow>(
  prev: PagedData<Row>,
  args: BucketPatchArgs,
  valueRaw: string,
  eventTs: number,
  clock: ChartClock,
): PagedData<Row> {
  if (!/^-?\d+$/.test(valueRaw)) return prev;
  const valueInteger = BigInt(valueRaw);
  const valueStr = valueRaw;

  const windowSecs = resolveChartIntervalSeconds(args, prev.bucketSeconds);
  if (windowSecs === null) return prev;
  // `to` bounds only the REST snapshot. Delayed NATS events may correct an
  // earlier bucket and newer events may extend a mounted chart past `to`.
  if (!eventIsAtOrAfterQueryStart(args, eventTs)) return prev;
  const tsBucket = bucketStartSeconds(eventTs, windowSecs);
  const cutoff =
    args.liveWindowSeconds === undefined ? undefined : clock.nowSeconds() - args.liveWindowSeconds;
  const rows = prev.data;
  const firstInWindow =
    cutoff === undefined ? 0 : rows.findIndex((row) => Date.parse(row.bucket) / 1000 >= cutoff);
  const keepFrom =
    cutoff === undefined ? 0 : Math.max(0, (firstInWindow < 0 ? rows.length : firstInWindow) - 1);
  const retained = keepFrom > 0 ? { ...prev, data: rows.slice(keepFrom) } : prev;
  const bounded = extendChartBounds(retained, tsBucket, windowSecs);
  const data = bounded.data;

  const isOhlcv = args.chartType === 'ohlcv';
  if (data.length === 0) {
    const bucketIso = new Date(tsBucket * 1000).toISOString();
    const firstRow = isOhlcv
      ? ({
          bucket: bucketIso,
          open: valueStr,
          high: valueStr,
          low: valueStr,
          close: valueStr,
        } as Row)
      : ({ bucket: bucketIso, value: valueStr } as Row);
    return { ...bounded, data: [firstRow] };
  }

  const existingIndex = data.findIndex((row) => parseBucketSeconds(row.bucket) === tsBucket);
  if (existingIndex >= 0) {
    const existingRow = data[existingIndex] as Row;
    if (isOhlcv) {
      if (!('open' in existingRow)) return bounded;
      const high = existingRow.high;
      const low = existingRow.low;
      const nextHigh = valueInteger > BigInt(high) ? valueStr : high;
      const nextLow = valueInteger < BigInt(low) ? valueStr : low;
      if (existingRow.close === valueStr && nextHigh === high && nextLow === low) {
        return bounded;
      }
      const patched = {
        ...existingRow,
        close: valueStr,
        high: nextHigh,
        low: nextLow,
      };
      const nextData = data.slice();
      nextData[existingIndex] = patched as Row;
      return { ...bounded, data: nextData };
    }
    if (!('value' in existingRow)) return bounded;
    if (existingRow.value === valueStr) return bounded;
    const patched = { ...existingRow, value: valueStr };
    const nextData = data.slice();
    nextData[existingIndex] = patched as Row;
    return { ...bounded, data: nextData };
  }

  const insertAt = data.findIndex((row) => {
    const rowBucket = parseBucketSeconds(row.bucket);
    return rowBucket !== null && rowBucket > tsBucket;
  });
  const bucketIso = new Date(tsBucket * 1000).toISOString();
  const newRow = isOhlcv
    ? ({ bucket: bucketIso, open: valueStr, high: valueStr, low: valueStr, close: valueStr } as Row)
    : ({ bucket: bucketIso, value: valueStr } as Row);
  const nextData = data.slice();
  nextData.splice(insertAt < 0 ? nextData.length : insertAt, 0, newRow);
  return { ...bounded, data: nextData };
}

function extendChartBounds<Row>(
  prev: PagedData<Row>,
  eventBucketSeconds: number,
  widthSeconds: number,
): PagedData<Row> {
  const endMs = prev.rangeEnd === undefined ? Number.NaN : Date.parse(prev.rangeEnd);
  const closedBeforeMs =
    prev.closedBefore === undefined ? Number.NaN : Date.parse(prev.closedBefore);
  const eventBucketMs = eventBucketSeconds * 1000;
  const nextEndMs = eventBucketMs + widthSeconds * 1000;
  const extendsEnd = Number.isFinite(endMs) && nextEndMs > endMs;
  const extendsClosed = Number.isFinite(closedBeforeMs) && eventBucketMs > closedBeforeMs;
  if (!extendsEnd && !extendsClosed) return prev;
  return {
    ...prev,
    ...(extendsEnd ? { rangeEnd: new Date(nextEndMs).toISOString() } : {}),
    ...(extendsClosed ? { closedBefore: new Date(eventBucketMs).toISOString() } : {}),
  };
}
